import React, { useEffect, useRef, useState } from 'react';

import { useNavigate } from 'react-router-dom';

import { useToast } from 'src/context/ToastContext';

import { useRerunStatementMutation } from '../api';
import { useAnalysisData } from '../hooks/useAnalysisData';
import { useOnboarding } from '../hooks/useOnboarding';
import { formatRatePlanType } from '../utils/utils';

import AnalysisHeader from './analysis/AnalysisHeader';
import AnalysisStep from './analysis/AnalysisStep';
import DocumentCheckBanner, {
	blocksQuote,
} from './analysis/DocumentCheckBanner';
import DocumentViewerPanel from './analysis/DocumentViewerPanel';
import ProcessingStep from './analysis/ProcessingStep';
import { Button, Modal, ModalBody, ModalFooter } from './common';

import type { ComparisonStatement } from '../types';

interface Document {
	id: string;
	name: string;
	type: string;
	date: string;
	size: string;
	status: string;
	processingMonth: string;
	pdfUrl?: string;
	results?: any;
}

interface AnalysisViewProps {
	document: Document;
	onBack: () => void;
	comparisonData?: ComparisonStatement | null;
	onRefresh?: () => void;
	onboardingContext?: boolean;
	onOnboardingComplete?: () => void;
}

const AnalysisView: React.FC<AnalysisViewProps> = ({
	document,
	onBack,
	onRefresh,
	comparisonData,
	onboardingContext = false,
	onOnboardingComplete,
}) => {
	const navigate = useNavigate();
	const { nextStep } = useOnboarding();
	const [isDocumentVisible, setIsDocumentVisible] = useState(false);
	const [headerHeight, setHeaderHeight] = useState(0);
	const [isRerunModalOpen, setIsRerunModalOpen] = useState(false);
	// ROC-62: "Keep my selection" dismisses the type-mismatch banner for this
	// statement only, so navigating to another statement re-evaluates it.
	const [dismissedMismatchForId, setDismissedMismatchForId] = useState<
		string | null
	>(null);
	const containerRef = useRef<HTMLDivElement>(null);
	const headerRef = useRef<HTMLDivElement>(null);

	const { showToast } = useToast();
	const [rerunStatement, { isLoading: isRerunning }] =
		useRerunStatementMutation();

	// View mode state for switching between Default/User Edits/Customized Quote views
	const [viewMode, setViewMode] = useState<
		'default' | 'user_edits' | 'customized_quote'
	>('default');

	// Use custom hook for all analysis data management
	const {
		localDocumentStatus,
		isLogsCollapsed,
		setIsLogsCollapsed,
		logsData,
		refetchLogs,
		refetch,
		refreshedDocument,
		isFetching,
		isLoading,
		analysisData,
		monthlySavingsData,
		calculatedTotals, // Backend-calculated totals (statement-type-aware)
		revenueAnalysis, // ISO Profit - reactive to line item edits
		enhancedDocument,
		showProgress,
		customRates,
		analysisType,
		actionType,
		// Statement-type mismatch (ROC-62)
		selectedRatePlanType,
		detectedRatePlanType,
		hasTypeMismatch,
		// Line item editing support
		handleLineItemUpdated,
		enableEditing,
		// View mode helpers
		hasUserEdits,
		hasCustomizedQuote,
	} = useAnalysisData({
		document,
		comparisonData,
		enableLineItemsFromDB: true,
		viewMode,
	});

	const toggleDocumentVisibility = () => {
		setIsDocumentVisible(!isDocumentVisible);
	};

	const handleCustomizeFeeStructure = (feeData: any) => {
		// Here you can implement the logic to:
		// 1. Update analysis calculations with custom fee structure
		// 2. Refresh the analysis data
		// 3. Show updated savings/costs
		// For now, just log the data
	};

	// Refresh everything after a Customize-Quote recalculation. Platform-engine
	// recalculation is synchronous (no processing -> completed transition to
	// drive the usual logs-poll refetch), so we explicitly refetch the logs,
	// the detailed document, and the line items table to surface the new
	// proposed totals / IC+ values without a manual reload.
	const handlePostRecalculation = () => {
		refetchLogs();
		if (refetch) {
			refetch();
		}
		handleLineItemUpdated();
	};

	// Calculate header height dynamically
	useEffect(() => {
		const updateHeaderHeight = () => {
			if (headerRef.current) {
				const height = headerRef.current.offsetHeight;
				setHeaderHeight(height);
			}
		};

		// Initial measurement
		updateHeaderHeight();

		// Update on window resize
		window.addEventListener('resize', updateHeaderHeight);

		// Use ResizeObserver to detect header size changes
		const resizeObserver = new ResizeObserver(() => {
			updateHeaderHeight();
		});

		if (headerRef.current) {
			resizeObserver.observe(headerRef.current);
		}

		// Also update after a short delay to ensure DOM is fully rendered
		const timeoutId = setTimeout(updateHeaderHeight, 100);

		return () => {
			window.removeEventListener('resize', updateHeaderHeight);
			resizeObserver.disconnect();
			clearTimeout(timeoutId);
		};
	}, [localDocumentStatus, analysisData, monthlySavingsData]);

	const handleOpenRerunModal = () => {
		if (localDocumentStatus === 'processing') {
			return;
		}
		setIsRerunModalOpen(true);
	};

	const handleCloseRerunModal = () => {
		setIsRerunModalOpen(false);
	};

	const handleConfirmRerun = async () => {
		try {
			await rerunStatement({
				fileId: document.id,
				skipCache: true,
				invalidateCache: true,
				deleteResults: false,
			}).unwrap();

			showToast(
				'Rerun Started',
				'We’re reprocessing this statement from scratch. This may take a few minutes.',
				'success',
			);

			setIsLogsCollapsed(false);
			setIsRerunModalOpen(false);
			refetchLogs();
			if (refetch) {
				refetch();
			}
		} catch (error: any) {
			const message =
				error?.data?.message ||
				error?.message ||
				'Failed to start rerun. Please try again.';
			showToast('Rerun Failed', message, 'error');
		}
	};

	// ROC-62: rerun as the classifier-detected type, loading that type's
	// default rate configuration ("Recalculate as {detected}" banner action).
	const handleRecalculateAsDetected = async () => {
		if (!detectedRatePlanType || localDocumentStatus === 'processing') {
			return;
		}
		try {
			await rerunStatement({
				fileId: document.id,
				skipCache: true,
				invalidateCache: true,
				deleteResults: false,
				targetRatePlanType: detectedRatePlanType,
			}).unwrap();

			showToast(
				'Recalculation Started',
				`We're reanalyzing this statement as ${formatRatePlanType(detectedRatePlanType)} with its default rate configuration.`,
				'success',
			);

			setIsLogsCollapsed(false);
			refetchLogs();
			if (refetch) {
				refetch();
			}
		} catch (error: any) {
			const message =
				error?.data?.message ||
				error?.message ||
				'Failed to start recalculation. Please try again.';
			showToast('Recalculation Failed', message, 'error');
		}
	};

	// ROC-72: completeness/readability verdict from the latest run.
	const documentCheck =
		refreshedDocument?.results?.document_check ??
		document.results?.document_check;
	// A bounced file: the banner already says why, so skip the failure log and empty analysis.
	const bounced =
		documentCheck?.status === 'flagged' && localDocumentStatus === 'failed';
	const hideAnalysis = blocksQuote(documentCheck) || bounced;

	// "Keep my selection" hides the banner for this statement (session-only).
	const showTypeMismatchBanner =
		hasTypeMismatch && dismissedMismatchForId !== document.id;

	return (
		<div ref={containerRef} className="flex flex-col bg-gray-50 min-w-[1200]">
			{/* Header Component - Fixed at top */}
			<div ref={headerRef} className="fixed top-[66px] left-0 right-0 z-10">
				<AnalysisHeader
					hideAnalysis={hideAnalysis}
					document={{
						id: document.id,
						name: document.name,
						date: document.date,
						status: localDocumentStatus,
					}}
					onBack={onBack}
					onToggleDocument={toggleDocumentVisibility}
					isDocumentVisible={isDocumentVisible}
					onboardingContext={onboardingContext}
					analysisData={analysisData}
					monthlySavingsData={monthlySavingsData}
					calculatedTotals={calculatedTotals}
					revenueAnalysis={revenueAnalysis}
					analysisType={analysisType}
					actionType={actionType}
					logsData={logsData}
					onCustomizeFeeStructure={handleCustomizeFeeStructure}
					isLoading={isLoading}
					isFetching={isFetching}
					onRefetchData={handlePostRecalculation}
					customRates={customRates}
					onRerun={
						localDocumentStatus !== 'processing'
							? handleOpenRerunModal
							: undefined
					}
					isRerunning={isRerunning}
					viewMode={viewMode}
					onViewModeChange={setViewMode}
					hasUserEdits={hasUserEdits}
					hasCustomizedQuote={hasCustomizedQuote}
					typeMismatch={
						showTypeMismatchBanner && detectedRatePlanType
							? {
									detectedRatePlanType,
									selectedRatePlanType: selectedRatePlanType ?? '',
									onRecalculateAsDetected: handleRecalculateAsDetected,
									onDismiss: () => setDismissedMismatchForId(document.id),
									isRecalculating: isRerunning,
								}
							: undefined
					}
				/>
			</div>

			{/* Main Content Area - Dynamic top padding based on header height */}
			<div
				className="flex-1 bg-gray-50 relative z-2"
				style={{
					paddingTop: headerHeight > 0 ? `${headerHeight}px` : '80px',
				}}
			>
				<div className="space-y-0">
					{documentCheck?.status === 'flagged' && (
						<div className="px-6 pt-4">
							<DocumentCheckBanner check={documentCheck} />
						</div>
					)}
					{/* Step 1: Processing Logs */}
					{!bounced && (
						<ProcessingStep
							isCollapsed={isLogsCollapsed}
							onToggle={() => setIsLogsCollapsed(!isLogsCollapsed)}
							status={localDocumentStatus}
							logsData={logsData}
							onRetry={refetchLogs}
							onRerun={handleOpenRerunModal}
							isRerunning={isRerunning}
						/>
					)}

					{/* Step 2: Fee Analysis (hidden when the file is not a statement) */}
					{!hideAnalysis && (
						<AnalysisStep
							status={localDocumentStatus}
							showProgress={showProgress}
							isLoading={isLoading}
							isFetching={isFetching}
							analysisData={analysisData}
							document={document}
							monthlySavingsData={monthlySavingsData}
							calculatedTotals={calculatedTotals}
							logsData={logsData}
							enableEditing={enableEditing}
							onLineItemUpdated={handleLineItemUpdated}
							viewMode={viewMode}
							onViewModeChange={setViewMode}
						/>
					)}
				</div>
			</div>

			{/* Document Viewer Panel */}
			<DocumentViewerPanel
				isVisible={isDocumentVisible}
				onClose={toggleDocumentVisibility}
				document={{
					...enhancedDocument,
					name: document.name,
					type: document.type,
					date: document.date,
					size: document.size,
				}}
			/>

			<Modal
				isOpen={isRerunModalOpen}
				onClose={handleCloseRerunModal}
				size="md"
				title="Rerun Analysis"
			>
				<ModalBody>
					<p className="text-sm text-gray-600">
						We'll start the analysis over from the beginning (not using old
						info). You'll still see your current results while we work, and it
						won't cost extra credits. Do you want to continue?
					</p>
				</ModalBody>
				<ModalFooter>
					<Button
						variant="outline"
						onClick={handleCloseRerunModal}
						disabled={isRerunning}
					>
						Cancel
					</Button>
					<Button
						variant="primary"
						onClick={handleConfirmRerun}
						loading={isRerunning}
					>
						Confirm Rerun
					</Button>
				</ModalFooter>
			</Modal>
		</div>
	);
};

export default AnalysisView;
